import React from 'react';
import { Users } from 'lucide-react';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';

export const LUNCH = '#f59e0b';
export const DINNER = '#7c3aed';
export const PREV = '#9ca3af';
export const shortDate = (d) => { const [, m, day] = d.split('-'); return `${day}/${m}`; };
export const weekday = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short' });
const LABELS = { lunch_covers: 'Midi', dinner_covers: 'Soir', prev_total_covers: 'Semaine précédente' };

// Fusionne la période affichée et la période précédente (alignée jour pour jour, mêmes jours de semaine)
export const mergePrev = (byDay, byDayPrev) => byDay.map((d, i) => {
  const p = byDayPrev?.[i];
  return p ? { ...d, prev_date: p.date, prev_total_covers: p.total_covers, prev_lunch_covers: p.lunch_covers, prev_dinner_covers: p.dinner_covers, prev_total_ticket: p.total_ticket, prev_lunch_ticket: p.lunch_ticket, prev_dinner_ticket: p.dinner_ticket } : d;
});

const Delta = ({ now, prev }) => {
  if (prev == null || !prev) return null;
  const pct = Math.round(((now - prev) / prev) * 100);
  return <span className={pct >= 0 ? 'text-emerald-700' : 'text-red-700'}> ({pct >= 0 ? '+' : ''}{pct} %)</span>;
};

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs" data-testid="covers-chart-tooltip">
      <p className="font-semibold text-gray-900 mb-1">{weekday(label)} {shortDate(label)}</p>
      <p style={{ color: LUNCH }}>Midi : <b>{row.lunch_covers}</b> convives · {row.lunch_orders} cmd · {Math.round(row.lunch_revenue)} CHF</p>
      <p style={{ color: DINNER }}>Soir : <b>{row.dinner_covers}</b> convives · {row.dinner_orders} cmd · {Math.round(row.dinner_revenue)} CHF</p>
      <p className="text-gray-700 mt-1">Total : <b>{row.total_covers}</b> convives<Delta now={row.total_covers} prev={row.prev_total_covers} /></p>
      {row.prev_date && <p className="text-gray-400">Sem. précédente ({weekday(row.prev_date)} {shortDate(row.prev_date)}) : {row.prev_total_covers} convives · midi {row.prev_lunch_covers} · soir {row.prev_dinner_covers}</p>}
    </div>
  );
};

export const CoversByDayChart = ({ byDay, byDayPrev, service }) => {
  if (!byDay?.length) return null;
  const data = mergePrev(byDay, byDayPrev);
  const hasPrev = data.some(d => d.prev_date);
  const dim = (svc) => (service && service !== svc ? 0.25 : 1);
  const peak = byDay.reduce((m, d) => (d.total_covers > m.total_covers ? d : m), byDay[0]);
  const curTotal = byDay.reduce((n, d) => n + d.total_covers, 0);
  const prevTotal = (byDayPrev || []).reduce((n, d) => n + d.total_covers, 0);
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-5" data-testid="covers-by-day-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-gray-700">
          <Users size={16} className="text-purple-600" />
          <span className="text-xs font-bold uppercase tracking-wider">Convives par jour · midi / soir</span>
        </div>
        <p className="text-xs text-gray-400" data-testid="covers-chart-peak">
          Pic : {weekday(peak.date)} {shortDate(peak.date)} · {peak.total_covers} convives
          {hasPrev && <span data-testid="covers-chart-prev-total"> · sem. précédente : {prevTotal} convives<Delta now={curTotal} prev={prevTotal} /></span>}
        </p>
      </div>
      <div className="h-56 md:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 14, right: 8, left: -18, bottom: 4 }} barCategoryGap={data.length > 20 ? '20%' : '35%'}>
            <CartesianGrid vertical={false} stroke="#f3f4f6" />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} interval={data.length > 14 ? Math.ceil(data.length / 10) - 1 : 0} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <Tooltip content={<TooltipBox />} cursor={{ fill: '#f9fafb' }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} formatter={(v) => LABELS[v] || v} />
            <Bar dataKey="lunch_covers" stackId="c" fill={LUNCH} fillOpacity={dim('lunch')} isAnimationActive={false} />
            <Bar dataKey="dinner_covers" stackId="c" fill={DINNER} fillOpacity={dim('dinner')} radius={[6, 6, 0, 0]} isAnimationActive={false} />
            {hasPrev && <Line type="monotone" dataKey="prev_total_covers" stroke={PREV} strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3, fill: '#fff', strokeWidth: 2 }} isAnimationActive={false} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CoversByDayChart;
